import {
  SLIME_PALETTES,
  paletteById,
} from "./identity";
import type {
  AppearanceOverride,
  AppearanceOverrides,
  SlimeAppearance,
  SlimeHatId,
  SlimePaletteId,
  SlimePatternId,
} from "./types";

const HAT_IDS = new Set<SlimeHatId>([
  "none",
  "hat.cap.blue",
  "hat.party.purple",
  "hat.straw",
  "hat.ribbon.red",
  "hat.sprout",
  "wearable.sunglasses",
  "wearable.top-hat",
  "wearable.beanie",
  "wearable.cowboy",
  "wearable.frog-hood",
  "wearable.headphones",
  "wearable.chef",
  "wearable.flower-crown",
  "wearable.devil-horns",
  "wearable.angel-halo",
  "wearable.bow-tie",
  "wearable.winter-scarf",
]);

const PATTERN_IDS = new Set<SlimePatternId>([
  "plain",
  "speckle",
  "stripe",
]);

const PALETTE_IDS = new Set(SLIME_PALETTES.map(({ id }) => id));
const HEX_COLOR = /^#[0-9A-Fa-f]{6}$/;
const MAX_OVERRIDE_KEY_LENGTH = 96;
const MAX_NICKNAME_CODEPOINTS = 12;
const UNSAFE_KEYS = new Set(["__proto__", "prototype", "constructor"]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function normalizeHexColor(value: unknown): string | undefined {
  if (typeof value !== "string" || !HEX_COLOR.test(value)) {
    return undefined;
  }

  return value.toUpperCase();
}

function normalizeNickname(value: unknown): string | undefined {
  if (typeof value !== "string") {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  return [...trimmed].slice(0, MAX_NICKNAME_CODEPOINTS).join("");
}

export function normalizeAppearanceOverride(
  input: unknown,
): AppearanceOverride {
  if (!isRecord(input)) {
    return {};
  }

  const result: AppearanceOverride = {};

  if (
    typeof input.paletteId === "string" &&
    PALETTE_IDS.has(input.paletteId as SlimePaletteId)
  ) {
    result.paletteId = input.paletteId as SlimePaletteId;
  }

  const bodyColor = normalizeHexColor(input.bodyColor);
  if (bodyColor) {
    result.bodyColor = bodyColor;
  }

  const accentColor = normalizeHexColor(input.accentColor);
  if (accentColor) {
    result.accentColor = accentColor;
  }

  if (
    typeof input.patternId === "string" &&
    PATTERN_IDS.has(input.patternId as SlimePatternId)
  ) {
    result.patternId = input.patternId as SlimePatternId;
  }

  if (
    typeof input.hatId === "string" &&
    HAT_IDS.has(input.hatId as SlimeHatId)
  ) {
    result.hatId = input.hatId as SlimeHatId;
  }

  const nickname = normalizeNickname(input.nickname);
  if (nickname) {
    result.nickname = nickname;
  }

  return result;
}

/**
 * Sanitizes untrusted localStorage JSON while dropping unknown fields and
 * prototype-pollution keys. Keys may be instrument IDs or ticker symbols.
 */
export function normalizeAppearanceOverrides(
  input: unknown,
): AppearanceOverrides {
  if (!isRecord(input)) {
    return {};
  }

  const result: AppearanceOverrides = {};

  for (const [rawKey, rawOverride] of Object.entries(input)) {
    const key = rawKey.trim();
    if (
      !key ||
      key.length > MAX_OVERRIDE_KEY_LENGTH ||
      UNSAFE_KEYS.has(key)
    ) {
      continue;
    }

    const normalized = normalizeAppearanceOverride(rawOverride);
    if (Object.keys(normalized).length > 0) {
      result[key] = normalized;
    }
  }

  return result;
}

export function parseAppearanceOverrides(
  serialized: string | null | undefined,
): AppearanceOverrides {
  if (!serialized) {
    return {};
  }

  try {
    return normalizeAppearanceOverrides(JSON.parse(serialized));
  } catch {
    return {};
  }
}

export function serializeAppearanceOverrides(
  overrides: AppearanceOverrides,
): string {
  const normalized = normalizeAppearanceOverrides(overrides);
  const sorted = Object.fromEntries(
    Object.entries(normalized).sort(([left], [right]) =>
      left.localeCompare(right),
    ),
  );

  return JSON.stringify(sorted);
}

export function applyAppearanceOverride(
  base: SlimeAppearance,
  rawOverride: AppearanceOverride | undefined,
): SlimeAppearance {
  const override = normalizeAppearanceOverride(rawOverride);
  const palette = override.paletteId
    ? paletteById(override.paletteId)
    : undefined;

  const next: SlimeAppearance = {
    ...base,
    ...(override.paletteId && { paletteId: override.paletteId }),
    ...(palette && {
      bodyColor: palette.bodyColor,
      accentColor: palette.accentColor,
    }),
    ...(override.bodyColor && { bodyColor: override.bodyColor }),
    ...(override.accentColor && { accentColor: override.accentColor }),
    ...(override.patternId && { patternId: override.patternId }),
    ...(override.nickname && { nickname: override.nickname }),
  };

  if (override.hatId === "none") {
    delete next.hatId;
  } else if (override.hatId) {
    next.hatId = override.hatId;
  }

  return next;
}
